import { SAMPLE_BASE_URL, SAMPLE_MAPS } from '../config/sample-maps';
import { button, el } from './dom';
import { t } from '../i18n';

/**
 * "Download all samples for offline" — fetches every recorded sample once so the
 * service worker's CacheFirst rule stores it. After that (and with the app shell
 * precached) the synth plays fully offline. A flag remembers a completed pull.
 */

const FLAG_KEY = 'webSynth.offlineReady.v1';

/** Every sample URL across every instrument in the library. */
export function allSampleUrls(): string[] {
  const urls: string[] = [];
  for (const [instrument, table] of Object.entries(SAMPLE_MAPS)) {
    for (const file of Object.values(table as Record<string, string>)) {
      urls.push(`${SAMPLE_BASE_URL}${instrument}/${file}`);
    }
  }
  return urls;
}

async function fetchAll(
  urls: readonly string[],
  concurrency: number,
  onProgress: (done: number, total: number, failed: number) => void,
): Promise<{ failed: number }> {
  const total = urls.length;
  let next = 0;
  let done = 0;
  let failed = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const i = next++;
      if (i >= total) return;
      const url = urls[i]!;
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(String(res.status));
        await res.arrayBuffer(); // drain so the cached response is complete
      } catch {
        failed++;
      }
      done++;
      onProgress(done, total, failed);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, total) }, worker));
  return { failed };
}

/** Ask the browser not to evict our cache — important on mobile. */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (navigator.storage?.persist) return await navigator.storage.persist();
  } catch {
    /* ignore */
  }
  return false;
}

export function installOfflineUi(): void {
  const btn = button('offlineDownloadBtn');
  const bar = el('offlineBar');
  const status = el('offlineStatus');
  let running = false;

  const setBar = (frac: number): void => bar.style.setProperty('--p', `${Math.round(frac * 100)}%`);
  const markDone = (): void => {
    try {
      localStorage.setItem(FLAG_KEY, '1');
    } catch {
      /* ignore */
    }
  };
  const wasDone = (): boolean => {
    try {
      return localStorage.getItem(FLAG_KEY) === '1';
    } catch {
      return false;
    }
  };

  if (wasDone()) {
    status.textContent = t('offline.done');
    btn.textContent = t('offline.refresh');
    setBar(1);
  }

  btn.addEventListener('click', async () => {
    if (running) return;
    running = true;
    btn.disabled = true;
    await requestPersistentStorage();

    const urls = allSampleUrls();
    status.textContent = t('offline.downloading', { done: 0, total: urls.length, failed: '' });
    setBar(0);

    const { failed } = await fetchAll(urls, 8, (done, total, fails) => {
      const suffix = fails ? t('offline.failedSuffix', { failed: fails }) : '';
      status.textContent = t('offline.downloading', { done, total, failed: suffix });
      setBar(done / total);
    });

    if (failed === 0) {
      markDone();
      status.textContent = t('offline.doneAll', { total: urls.length });
      btn.textContent = t('offline.refresh');
    } else {
      status.textContent = t('offline.doneWithErrors', { failed });
    }
    setBar(1);
    btn.disabled = false;
    running = false;
  });
}
